// Pure itinerary logic — no React. Shared by ItineraryScreen (editor) and
// SharedItineraryView (public share page); unit-tested in packItinerary.test.js.
//
// Two layout modes (see docs/state-model.md):
//  - AUTO:   packItinerary(selectedIds, dayCount, dateStart, allIdeas) packs
//            ideas into days (10h/day cap), sorts each day by time-of-day
//            bucket (todBucket) then category, and assigns sequential times
//            (6am start for sunrise-led days; evening items anchored >= 5pm).
//  - MANUAL: buildDaysFromPlan(plan, ...) renders a user-ordered string[][]
//            of idea ids exactly as saved (trip.customPlan, JSON string).
//
// Times are decimal hours (13.5 = 1:30pm). fillEmptySlots redistributes
// unplaced selected ideas into open manual-plan days.
import { computeLegSchedule } from '../../utils/legSchedule';

export function getDayLabels(dateStart, count) {
  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  try {
    let base;
    if (dateStart && dateStart.includes('-')) {
      base = new Date(dateStart + 'T12:00:00');
    } else if (dateStart) {
      const parts = dateStart.split(' ');
      const mIdx = MONTHS.indexOf(parts[0]);
      if (mIdx >= 0 && parts[1]) base = new Date(new Date().getFullYear(), mIdx, parseInt(parts[1], 10));
    }
    if (!base || isNaN(base)) throw new Error('invalid');
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(base); d.setDate(base.getDate() + i);
      return { date: `${d.getDate()} ${MONTHS[d.getMonth()]}`, label: DAYS[d.getDay()] };
    });
  } catch {
    return Array.from({ length: count }, (_, i) => ({ date: `Day ${i + 1}`, label: '' }));
  }
}

// dayCaps: optional array of per-day hour budgets; defaults to 10 for each day.
// Pass reduced caps for days with flights (arrival day = hours after landing, departure day = hours before takeoff).
export function fillEmptySlots(localPlan, selectedIds, allIdeas = [], overrides = {}, dayCaps = null) {
  const DEFAULT_CAP = 10;
  const placed = new Set(localPlan.flat());
  const unplaced = selectedIds
    .filter(id => !placed.has(id))
    .map(id => allIdeas.find(i => i.id === id))
    .filter(Boolean)
    .sort((a, b) => {
      const aDur = overrides[a.id]?.dur ?? a.dur;
      const bDur = overrides[b.id]?.dur ?? b.dur;
      return bDur - aDur;
    });
  if (unplaced.length === 0) return localPlan;
  const dayUsed = localPlan.map(row =>
    row.reduce((sum, id) => {
      const idea = allIdeas.find(i => i.id === id);
      if (!idea) return sum;
      return sum + (overrides[id]?.dur ?? idea.dur);
    }, 0)
  );
  const newPlan = localPlan.map(d => [...d]);
  for (const idea of unplaced) {
    const dur = overrides[idea.id]?.dur ?? idea.dur;
    let target = -1, bestFit = -1;
    for (let d = 0; d < newPlan.length; d++) {
      const cap = dayCaps ? (dayCaps[d] ?? DEFAULT_CAP) : DEFAULT_CAP;
      const remain = cap - dayUsed[d];
      if (dur <= remain && remain > bestFit) { bestFit = remain; target = d; }
    }
    if (target === -1) continue;
    newPlan[target].push(idea.id);
    dayUsed[target] += dur;
  }
  return newPlan;
}

// A leg/date edit changing the trip's total dayCount used to blow away the
// whole manual plan (dropping back to AUTO mode, which bin-packs every
// selected idea into a day — silently placing parking-lot items the user
// had deliberately left unplaced). Resize the row count instead: pad with
// empty rows if the trip grew, drop trailing rows if it shrank. Ideas in a
// dropped row simply fall back to the parking lot, same as removing them
// from any other day — nothing is deleted or auto-placed.
export function resizePlanRows(localPlan, dayCount) {
  if (dayCount > localPlan.length) {
    return [...localPlan, ...Array.from({ length: dayCount - localPlan.length }, () => [])];
  }
  return localPlan.slice(0, dayCount);
}

// Time-of-day bucket from title/meta keywords: 0 = morning, 1 = flexible, 2 = evening.
// Keeps sunrise walks out of midday slots and dinner/nightlife out of the afternoon.
export function todBucket(idea) {
  const text = `${idea.title || ''} ${idea.meta || ''}`.toLowerCase();
  if (/sunrise|dawn|breakfast|brunch|morning/.test(text)) return 0;
  if (/sunset|evening|night|dinner|cocktail|pub crawl|bar\b|beers/.test(text) || idea.cat === 'Nightlife') return 2;
  return 1;
}

function packDays(ideas, dayCount, labels) {
  const days = Array.from({ length: dayCount }, (_, i) => ({
    date: labels[i]?.date, label: labels[i]?.label, items: [], used: 0,
  }));
  const DAY_CAP = 10;
  const overflow = [];
  for (const it of ideas.slice().sort((a, b) => b.dur - a.dur)) {
    let target = -1, bestFit = -1;
    for (let d = 0; d < days.length; d++) {
      const remain = DAY_CAP - days[d].used;
      if (it.dur <= remain && remain > bestFit) { bestFit = remain; target = d; }
    }
    if (target === -1) { overflow.push(it.id); continue; }
    days[target].items.push(it);
    days[target].used += it.dur;
  }
  days.forEach(d => {
    d.items.sort((a, b) => {
      const order = { Food: 1, Sights: 2, Experience: 3, 'Day trip': 0, Nightlife: 4, Shopping: 2 };
      const tod = todBucket(a) - todBucket(b);
      if (tod !== 0) return tod;
      const dawn = x => (/sunrise|dawn/i.test(x.title || '') ? 0 : 1);
      if (dawn(a) !== dawn(b)) return dawn(a) - dawn(b);
      return (order[a.cat] || 5) - (order[b.cat] || 5);
    });
    // Sunrise-ish first items start the day early; evening items never start
    // before 5pm even when the afternoon is open.
    let t = d.items[0] && todBucket(d.items[0]) === 0 && /sunrise|dawn/i.test(d.items[0].title || '') ? 6 : 9;
    d.items = d.items.map(idea => {
      if (todBucket(idea) === 2) t = Math.max(t, 17);
      const start = t; t += idea.dur;
      return { idea, start, end: t };
    });
  });
  return { days, overflow };
}

export function packItinerary(selectedIds, dayCount = 5, dateStart, allIdeas = []) {
  const ideas = allIdeas.filter(i => selectedIds.includes(i.id));
  const labels = getDayLabels(dateStart, dayCount);
  return packDays(ideas, dayCount, labels);
}

// Multi-city trips: legs is [{ destination, dayCount }, ...]. Each leg's day
// block is packed independently from only that leg's ideas (allIdeas items
// tagged with legIndex by data.js#getIdeasForLegs), then concatenated so day
// indices stay contiguous across the whole trip. Falls back to plain
// packItinerary for single-leg/no-leg trips.
export function packMultiLegItinerary(legs, selectedIds, dateStart, allIdeas = []) {
  if (!legs || legs.length <= 1) {
    return packItinerary(selectedIds, legs?.[0]?.dayCount, dateStart, allIdeas);
  }
  const { cardCounts, totalDays } = computeLegSchedule(legs);
  const labels = getDayLabels(dateStart, totalDays);
  const selectedSet = new Set(selectedIds);
  let cursor = 0;
  const days = [];
  const overflow = [];
  legs.forEach((leg, legIndex) => {
    const legDayCount = cardCounts[legIndex];
    const legLabels = labels.slice(cursor, cursor + legDayCount);
    const legIdeas = allIdeas.filter(i => selectedSet.has(i.id) && i.legIndex === legIndex);
    const { days: legDays, overflow: legOverflow } = packDays(legIdeas, legDayCount, legLabels);
    legDays.forEach(d => { d.destCity = (leg.destination || '').split(',')[0].trim(); });
    days.push(...legDays);
    overflow.push(...legOverflow);
    cursor += legDayCount;
  });
  // Selected ideas with no legIndex at all (untagged custom ideas) never
  // match any leg above — surface them as overflow/parking-lot instead of
  // guessing a city for them (see App.js#ideasForTrip).
  allIdeas.forEach(i => {
    if (selectedSet.has(i.id) && i.legIndex == null) overflow.push(i.id);
  });
  return { days, overflow };
}

// Maps a flat day index (0-based) to which leg it belongs to, for manual
// (customPlan) mode where the plan is a flat string[][] with no per-day city.
export function legIndexForDay(legs, dayIndex) {
  if (!legs || legs.length <= 1) return 0;
  const { cardCounts } = computeLegSchedule(legs);
  let cursor = 0;
  for (let i = 0; i < legs.length; i++) {
    cursor += cardCounts[i];
    if (dayIndex < cursor) return i;
  }
  return legs.length - 1;
}

export function buildDaysFromPlan(plan, dateStart, dayCount, allIdeas, overrides = {}, legs = null) {
  const labels = getDayLabels(dateStart, dayCount);
  return plan.map((ideaIds, di) => {
    let t = 9;
    const items = (ideaIds || []).map(id => {
      const idea = allIdeas.find(i => i.id === id);
      if (!idea) return null;
      const ov = overrides[id] || {};
      const dur = ov.dur ?? idea.dur;
      // Fixed-time items anchor the clock to their pinned time (never go backward)
      if (ov.fixedTime && ov.startTime !== undefined) t = Math.max(t, ov.startTime);
      const start = t;
      t += dur;
      return { idea, start, end: t };
    }).filter(Boolean);
    const day = {
      date: labels[di]?.date || `Day ${di + 1}`,
      label: labels[di]?.label || '',
      items,
      used: items.reduce((s, slot) => s + (slot.end - slot.start), 0),
    };
    if (legs && legs.length > 1) {
      const leg = legs[legIndexForDay(legs, di)];
      day.destCity = (leg?.destination || '').split(',')[0].trim();
    }
    return day;
  });
}

export const fmtTime = (h) => {
  const rawHr = Math.floor(h);
  const min = Math.round((h - rawHr) * 60);
  // Same hour-24-is-midnight normalization as toTimeStr above.
  const hr = rawHr % 24;
  const ampm = hr >= 12 ? 'pm' : 'am';
  const h12 = hr > 12 ? hr - 12 : hr === 0 ? 12 : hr;
  return `${h12}${min ? ':' + String(min).padStart(2, '0') : ''}${ampm}`;
};

export function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function getTripLabel(trip) {
  if (!trip) return '';
  const city = trip.legs && trip.legs.length > 1
    ? trip.legs.map(l => (l.destination || '').split(',')[0].trim()).filter(Boolean).join(' → ')
    : (trip.destination?.split(',')[0] || '');
  const dates = trip.dateStart ? `${fmtDate(trip.dateStart)}${trip.dateEnd ? ` – ${fmtDate(trip.dateEnd)}` : ''}` : '';
  return [city, dates].filter(Boolean).join(' · ');
}

// ── Location search (Nominatim) ───────────────────────────────────────────────

export function toTimeStr(h) {
  const rawHr = Math.floor(h); const min = Math.round((h - rawHr) * 60);
  // An activity ending at the very end of the day is hour 24 (midnight),
  // which is a valid boundary value (see CalendarView's yToHourEnd) but
  // isn't itself a valid hour-of-day — normalize it back to hour 0 so it
  // reads "12:00 am" instead of the nonsensical "12:00 pm".
  const hr = rawHr % 24;
  const h12 = hr % 12 === 0 ? 12 : hr % 12;
  return `${h12}:${min.toString().padStart(2, '0')} ${hr >= 12 ? 'pm' : 'am'}`;
}

export function parseTimeStr(s) {
  const t = (s || '').trim().toLowerCase().replace(/\s+/g, '');
  let m;
  m = t.match(/^(\d{1,2})[:.](\d{2})\s*(am|pm)?$/);
  if (m) {
    let hr = parseInt(m[1], 10); const min = parseInt(m[2], 10); const suf = m[3];
    if (suf === 'pm' && hr !== 12) hr += 12;
    else if (suf === 'am' && hr === 12) hr = 0;
    else if (!suf && hr < 7) hr += 12;
    if (hr <= 23 && min <= 59) return hr + min / 60;
  }
  m = t.match(/^(\d{1,2})\s*(am|pm)$/);
  if (m) {
    let hr = parseInt(m[1], 10); const suf = m[2];
    if (suf === 'pm' && hr !== 12) hr += 12;
    else if (suf === 'am' && hr === 12) hr = 0;
    if (hr <= 23) return hr;
  }
  m = t.match(/^(\d{1,2})$/);
  if (m) { let hr = parseInt(m[1], 10); if (hr < 6) hr += 12; if (hr <= 23) return hr; }
  return null;
}


// Flight leg times (depTime/arrTime) are always already strict 24-hour
// "HH:MM" — from the scan API, or an <input type="time">. Unlike
// parseTimeStr (which guesses am/pm for free-text activity times, e.g.
// treating a bare "2" as 2pm), this never reinterprets the hour: "00:45"
// must stay 00:45, not get shifted 12 hours by an am/pm heuristic that
// doesn't apply to already-unambiguous 24-hour values.
export function parseFlightHM(s) {
  const m = (s || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hr = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (hr > 23 || min > 59) return null;
  return hr + min / 60;
}

export function parseFlightStr(str) {
  if (!str) return {};
  const times = str.match(/\b(\d{1,2}:\d{2})\b/g) || [];
  const hasArrow = /→|->/.test(str);
  let origin, dest;
  if (hasArrow) {
    const [before, after] = str.split(/→|->/);
    const bAirports = before.match(/\b([A-Z]{3})\b/g) || [];
    const aAirports = (after || '').match(/\b([A-Z]{3})\b/g) || [];
    origin = bAirports[bAirports.length - 1];
    dest = aAirports[0];
  } else {
    const airports = str.match(/\b([A-Z]{3})\b/g) || [];
    [origin, dest] = airports;
  }
  const flightNo = str.match(/\b([A-Z]{2}\s?\d{3,4})\b/)?.[1]?.replace(/\s/, '');
  const dateM = str.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  return { depTime: times[0], arrTime: times[1], origin, dest, flightNo, date: dateM?.[1] };
}



// Greedy lane assignment — returns Map(ideaId → { col, totalCols })
export function computeEventLanes(items, getStart, getDur) {
  if (!items.length) return new Map();
  const evs = items.map(s => ({ id: s.idea.id, start: getStart(s), end: getStart(s) + getDur(s) }))
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const layout = new Map();
  let i = 0;
  while (i < evs.length) {
    let groupEnd = evs[i].end;
    let j = i + 1;
    while (j < evs.length && evs[j].start < groupEnd) { groupEnd = Math.max(groupEnd, evs[j].end); j++; }
    const group = evs.slice(i, j);
    const lanes = [];
    for (const ev of group) {
      let placed = false;
      for (let l = 0; l < lanes.length; l++) {
        if (ev.start >= lanes[l]) { layout.set(ev.id, { col: l }); lanes[l] = ev.end; placed = true; break; }
      }
      if (!placed) { layout.set(ev.id, { col: lanes.length }); lanes.push(ev.end); }
    }
    const totalCols = lanes.length;
    for (const ev of group) { const inf = layout.get(ev.id); if (inf) inf.totalCols = totalCols; }
    i = j;
  }
  return layout;
}

